import React, { useState } from "react";
import { formatUserHandle } from "../../../utils/userHandle";
import { useConfirm } from "../../../ui/components/ConfirmDialog";
import { useResettleAdminSeasonMutation, useSettleAdminDayMutation } from "../api/gameApi";
import type { AdminSeason, AdminSettleResult } from "../api/types";
import { errorText, fmtDay, SKIP_TEXT } from "./adminText";
import { Btn, Input, Label, Notice, Row, Section, SectionTitle, Small, SubTitle } from "./adminStyles";

/** The last day that can be picked: today's AOTD day, or the season's last scoring day if that is earlier. */
export function lastSettleableDay(s: Pick<AdminSeason, "dayKeyTo" | "currentDayKey" | "endedDayKey">): string {
  const end = s.endedDayKey && s.endedDayKey < s.dayKeyTo ? s.endedDayKey : s.dayKeyTo;
  return s.currentDayKey < end ? s.currentDayKey : end;
}

function defaultDay(s: AdminSeason): string {
  const last = lastSettleableDay(s);
  if (last < s.dayKeyFrom) return s.dayKeyFrom;
  return last;
}

const SettleResult: React.FC<{ r: AdminSettleResult }> = ({ r }) => {
  if (r.skipped) {
    return (
      <Notice tone="warn" role="status">
        {fmtDay(r.dayKey)} was not settled. {SKIP_TEXT[r.skipped] ?? r.skipped}
      </Notice>
    );
  }
  return (
    <Notice tone="ok" role="status">
      <div>
        Settled {fmtDay(r.dayKey)}: {r.rows.length} {r.rows.length === 1 ? "player" : "players"} scored.{" "}
        {r.guessSettled ? "The guessing point is in." : "The guessing is still open, so its point is not in yet."}
        {r.revised ? " A final day changed." : ""}
      </div>
      {r.standingsTop.length > 0 && (
        <ol style={{ margin: "6px 0 0 18px", listStyle: "decimal" }}>
          {r.standingsTop.slice(0, 5).map((row) => (
            <li key={row.username}>
              {formatUserHandle(row.username, row.emoji)}: {row.total}
            </li>
          ))}
        </ol>
      )}
    </Notice>
  );
};

/** Settle one day by hand, or re-settle every settled day. Neither sends messages. */
const SettlePanel: React.FC<{ season: AdminSeason }> = ({ season }) => {
  const confirm = useConfirm();
  const [dayKey, setDayKey] = useState(() => defaultDay(season));
  const [settle, { isLoading: settling }] = useSettleAdminDayMutation();
  const [resettle, { isLoading: resettling }] = useResettleAdminSeasonMutation();
  const [result, setResult] = useState<AdminSettleResult | null>(null);
  const [resettled, setResettled] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSettle = season.phase === "active" || season.phase === "ended";
  const max = lastSettleableDay(season);
  const inRange = !!dayKey && dayKey >= season.dayKeyFrom && dayKey <= max;

  const runSettle = async () => {
    setError(null);
    setResult(null);
    setResettled(null);
    try {
      setResult(await settle({ id: season.id, dayKey }).unwrap());
    } catch (err) {
      setError(errorText(err, "Could not settle the day."));
    }
  };

  const runResettle = async () => {
    setError(null);
    setResult(null);
    setResettled(null);
    const ok = await confirm({
      title: "Re-settle every day?",
      message: `Recomputes all ${season.settledDays} settled days from the race records and rebuilds the standings. It sends no messages.`,
      confirmLabel: "Re-settle",
    });
    if (!ok) return;
    try {
      setResettled((await resettle(season.id).unwrap()).days);
    } catch (err) {
      setError(errorText(err, "Could not re-settle the season."));
    }
  };

  return (
    <Section aria-label="Scoring">
      <SectionTitle>Scoring</SectionTitle>
      <Small style={{ marginBottom: 12 }}>
        Days settle on their own when the art is out and again when the guessing closes; the hourly sweep catches
        anything missed. Settling here scores the day again and sends nothing.
      </Small>
      {!canSettle && (
        <Notice tone="warn">
          {season.phase === "archived"
            ? "The season is archived, so its days stay as they are."
            : "Days settle only once the season is running."}
        </Notice>
      )}
      <SubTitle>Settle a day</SubTitle>
      <Row>
        <Label style={{ flex: "0 1 220px" }}>
          AOTD day
          <Input
            type="date"
            aria-label="Day to settle"
            value={dayKey}
            min={season.dayKeyFrom}
            max={max}
            onChange={(e) => setDayKey(e.target.value)}
          />
        </Label>
        <Btn type="button" variant="primary" disabled={!canSettle || !inRange || settling} onClick={() => void runSettle()}>
          {settling ? "Settling…" : "Settle this day"}
        </Btn>
      </Row>
      {canSettle && !inRange && dayKey && (
        <Small>
          Pick a day from {fmtDay(season.dayKeyFrom)} to {fmtDay(max)}.
        </Small>
      )}
      {result && <SettleResult r={result} />}

      <SubTitle>Re-settle everything</SubTitle>
      <Row>
        <Btn
          type="button"
          disabled={!canSettle || season.settledDays === 0 || resettling}
          onClick={() => void runResettle()}
        >
          {resettling ? "Re-settling…" : "Re-settle every day"}
        </Btn>
      </Row>
      {resettled !== null && (
        <Notice tone="ok" role="status">
          Re-settled {resettled} {resettled === 1 ? "day" : "days"}.
        </Notice>
      )}
      {error && (
        <Notice tone="error" role="alert">
          {error}
        </Notice>
      )}
    </Section>
  );
};

export default SettlePanel;
